// A link that lands on the product list with a filter already on it was followed to
// see those products. The head of that page names the bare catalogue and says nothing
// about the filter, so the shopper reads it, finds nothing of theirs, and scrolls past
// it every time. These links open on the list instead: the click records a scroll
// request and PlpHeaderHolder answers it once the filtered list has rendered.
//
// Only the plain list is treated this way. An equivalent path (/fa/sunglasses/rayban)
// is a page of its own, with a title, a cover and chips written for it, and starts at
// its own top like any other page.

const PRODUCT_LIST_PATHS = ["/fa/products", "/fa/v2/products"];

// Sorting and paging show the same list over again, so a link carrying only these
// is not a filtered one.
const LIST_NARROWING_IGNORED_KEYS = ["sort", "page", "limit", "lp", "slug"];

// A campaign keeps its countdown in the head of the page, and that countdown is the
// reason the banner was followed, so those links still open where it is.
const KEYS_THAT_FILL_THE_HEAD = ["pd"];

// Whether following this link should carry the page down to the list on arrival.
export function opensProductListAtTheList(href: string | null | undefined, currentAddress: string): boolean {
    const target = parseInternalAddress(href);
    if (!target || !PRODUCT_LIST_PATHS.includes(target.path)) return false;

    const names = [...target.params.keys()];
    if (names.some(name => KEYS_THAT_FILL_THE_HEAD.includes(name))) return false;
    if (!names.some(name => !LIST_NARROWING_IGNORED_KEYS.includes(name))) return false;

    // A link pointing at the address already open changes nothing to scroll to.
    const current = parseInternalAddress(currentAddress);
    return !current || normalize(current) !== normalize(target);
}

type Address = { path: string; params: URLSearchParams };

// Site addresses only: an external link leaves the site, and an anchor or a mailto
// never names a page here.
function parseInternalAddress(href: string | null | undefined): Address | null {
    if (!href) return null;

    try {
        const address = href.startsWith("/")
            ? new URL(href, "http://vahdatoptic.local")
            : new URL(href);

        if (!href.startsWith("/") && address.origin !== window.location.origin) return null;

        return { path: decodePath(address.pathname), params: address.searchParams };
    } catch {
        return null;
    }
}

// A path written with its Persian segments encoded is the same path as one written
// with them spelled out, and a trailing slash is not a different page.
function decodePath(pathname: string): string {
    let path = pathname;
    try {
        path = decodeURIComponent(pathname);
    } catch {
        // Left as it came: a half written escape is not worth failing the match over.
    }

    return path.replace(/\/+$/, "") || "/";
}

// The same filters written in another order are the same address.
function normalize({ path, params }: Address): string {
    const entries = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
    return `${path}?${entries.map(([name, value]) => `${name}=${value}`).join("&")}`;
}
